import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { Figure, QuantileCells, ibamaCasesHref } from '@/pages/ibama/OverviewPage';
import { fetchIbamaPenalties } from '@/services/ibama';
import type { AnswerOut, IbamaLadderTable } from '@/services/types';

/**
 * IBAMA face values: the fine an auto was issued with, in reais.
 *
 * One ladder per group and one column: no IBAMA source publishes the amount
 * after either instance, so there is no "original vs final". The ledger's
 * ladders (nature, state) and the master's ladder (auto type) are two
 * populations, labelled by basis and never pooled. The pre-Real rows are
 * fenced out of every ladder and counted in the currency card.
 */
export function IbamaPenaltiesPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'penalties'],
    queryFn: fetchIbamaPenalties,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:penalties.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:penalties.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ibama:penalties.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ibama/export/penalties" label={t('ibama:penalties.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-corpus>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ibama:penalties.corpus.title')}
          </h2>
          <Answer answer={data.corpus} help={t('ibama:penalties.corpus.help')}>
            {(summary) => (
              <div className="space-y-2">
                <QuantileCells summary={summary} format={(v) => currency.format(v)} />
                <p className="text-xs text-muted-foreground">
                  {t('ibama:penalties.corpus.count', {
                    n: number.format(summary.n),
                    min: currency.format(summary.minimum),
                    max: currency.format(summary.maximum),
                  })}
                </p>
              </div>
            )}
          </Answer>
        </section>

        <section className="rounded-lg border bg-card p-4" data-currency>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ibama:penalties.currency.title')}
          </h2>
          <Answer answer={data.currency} help={t('ibama:penalties.currency.help')}>
            {(fence) => (
              <div className="grid grid-cols-2 gap-3">
                <Figure
                  label={t('ibama:penalties.currency.brl')}
                  value={number.format(fence.brl)}
                  href={ibamaCasesHref({ currency: 'brl' })}
                />
                <Figure
                  label={t('ibama:penalties.currency.other')}
                  value={number.format(fence.non_brl)}
                  href={fence.non_brl ? ibamaCasesHref({ currency: 'other' }) : undefined}
                />
              </div>
            )}
          </Answer>
        </section>
      </div>

      <FieldGuide i18nKey="ibama:penalties.guide" level={2} />

      <LadderTable answer={data.by_nature} />
      <LadderTable answer={data.by_uf} />

      <section className="space-y-3" data-master>
        <h2 className="text-sm font-semibold text-foreground">
          {t('ibama:penalties.master.title')}
        </h2>
        <p className="text-xs text-muted-foreground">{t('ibama:penalties.master.intro')}</p>
        <LadderTable answer={data.by_auto_type} />
        <div className="grid gap-4 lg:grid-cols-2">
          <OutcomeMixPanel
            answer={{ ...data.auto_types, value: data.auto_types.value?.types ?? null }}
            titleKey="ibama:penalties.autoTypes.title"
            helpKey="ibama:penalties.autoTypes.help"
            labelPrefix="ibama:penalties.autoTypeCodes."
          />
          <OutcomeMixPanel
            answer={{ ...data.gravity, value: data.gravity.value?.levels ?? null }}
            titleKey="ibama:penalties.gravity.title"
            helpKey="ibama:penalties.gravity.help"
            labelPrefix="ibama:penalties.gravityCodes."
          />
        </div>
      </section>
    </div>
  );
}

function LadderTable({ answer }: { answer: AnswerOut<IbamaLadderTable> }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const kind = answer.value?.kind ?? 'nature';
  const quantiles = ['p10', 'p25', 'p50', 'p75', 'p90'] as const;
  const href = (group: string) =>
    kind === 'nature'
      ? ibamaCasesHref({ nature: group, currency: 'brl' })
      : kind === 'uf'
        ? ibamaCasesHref({ uf: group, currency: 'brl' })
        : undefined;

  return (
    <section className="rounded-lg border bg-card p-4" data-ladder={kind}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`ibama:penalties.ladders.${kind}.title`)}
      </h2>
      <Answer answer={answer} help={t(`ibama:penalties.ladders.${kind}.help`)}>
        {(table) => (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground" data-basis={table.basis}>
              {t(`ibama:penalties.ladders.basis.${table.basis}`)}
            </p>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ibama:penalties.ladders.group')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:penalties.ladders.rows')}
                    </th>
                    {quantiles.map((q) => (
                      <th key={q} className="px-3 py-2 text-right font-medium">
                        {q}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => {
                    const to = href(row.group);
                    return (
                      <tr key={row.group} className="border-t" data-group={row.group}>
                        <td className="px-3 py-2 text-foreground">{row.group}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {to ? (
                            <Link
                              to={to}
                              className="text-primary underline-offset-2 hover:underline"
                            >
                              {number.format(row.cases)}
                            </Link>
                          ) : (
                            <span className="text-foreground">{number.format(row.cases)}</span>
                          )}
                        </td>
                        {quantiles.map((q) => (
                          <td key={q} className="px-3 py-2 text-right tabular-nums text-foreground">
                            {currency.format(row.face_value.quantiles[q] ?? 0)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {table.suppressed > 0 ? (
              <p className="text-xs text-muted-foreground" data-suppressed>
                {t('ibama:penalties.ladders.suppressed', {
                  groups: number.format(table.suppressed),
                  rows: number.format(table.suppressed_cases),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}
